import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactElement } from 'react';
import { Chess, type Move, type Square } from 'chess.js';
import type { ChessProviderProps, GameState, GameResult, MoveEvent } from '../types';
import { PieceTheme, PieceSymbol, DEFAULT_PIECES, UNICODE_PIECES } from '../types/pieces';
import { PIECE_SVGS } from '../utils/pieceConstants';
import { createEmptyPieceRecord } from '../utils/pieceUtils';

/** Chess game context interface defining the core chess functionality */
interface ChessContextType {
  game: Chess;
  position: string;
  isCheck: boolean;
  isCheckmate: boolean;
  isStalemate: boolean;
  isDraw: boolean;
  isThreefoldRepetition: boolean;
  isFiftyMoveRule: boolean;
  isInsufficientMaterial: boolean;
  turn: 'w' | 'b';
  history: string[];
  moveNumber: number;
  pgn: string;
  makeMove: (from: string, to: string, promotion?: PieceSymbol) => boolean;
  undo: () => boolean;
  redo: () => boolean;
  reset: () => void;
  getPossibleMoves: (square: string) => string[];
  exportPGN: () => string;
  importPGN: (pgn: string) => boolean;
  getGameState: () => GameState;
  validateFen: (fen: string) => boolean;
}

/** Combined context type that includes both chess and piece functionality */
interface CombinedContextType extends ChessContextType {
  currentTheme: string;
  themes: Record<string, PieceTheme>;
  loadTheme: (name: string) => Promise<void>;
  getPieceSvg: (piece: PieceSymbol) => string | null;
  setCustomPiece: (piece: PieceSymbol, url: string) => void;
  resetTheme: () => void;
  preloadTheme: (name: string) => Promise<void>;
  getThemeNames: () => string[];
  isThemeLoaded: (name: string) => boolean;
}

const ChessContext = createContext<CombinedContextType | null>(null);

export const useChess = () => {
  const context = useContext(ChessContext);
  if (!context) {
    throw new Error('useChess must be used within a ChessProvider');
  }
  return context;
};

export const usePieces = useChess;

export function ChessProvider({ 
  children, 
  initialPosition = 'start',
  enableUndo = true,
  onPositionChange,
  onGameEnd,
  persistState = false,
  defaultTheme = 'default',
  customPieces = createEmptyPieceRecord(),
  preloadThemes = false
}: ChessProviderProps): ReactElement {
  // Chess state
  const [game] = useState(() => new Chess(initialPosition === 'start' ? undefined : initialPosition));
  const [position, setPosition] = useState(game.fen());
  const [history, setHistory] = useState<string[]>([]);
  const [positionHistory, setPositionHistory] = useState<Map<string, number>>(new Map());
  const [moveNumber, setMoveNumber] = useState(1);
  const redoStack = useRef<Move[]>([]);

  // Piece state
  const [themes, setThemes] = useState<Record<string, PieceTheme>>({});
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const [loadingThemes, setLoadingThemes] = useState<Set<string>>(new Set());
  const [loadedThemes, setLoadedThemes] = useState<Set<string>>(new Set());

  const updateGameState = useCallback(() => {
    try {
      const newPosition = game.fen();
      setPosition(newPosition);
      setHistory(game.history({ verbose: true }).map((move: Move) => move.san));
      setMoveNumber(Math.floor(game.moveNumber() / 2) + 1);
      
      // Update position history for threefold repetition
      const positionKey = newPosition.split(' ').slice(0, 4).join(' ');
      setPositionHistory(prev => {
        const newMap = new Map(prev);
        newMap.set(positionKey, (newMap.get(positionKey) || 0) + 1);
        return newMap;
      });

      // Call position change callback
      onPositionChange?.(newPosition);

      // Check for game end conditions
      if (game.isGameOver()) {
        const result: GameResult = {
          winner: game.isCheckmate() 
            ? (game.turn() === 'w' ? 'black' : 'white')
            : game.isDraw() ? 'draw' : null,
          reason: game.isCheckmate() ? 'checkmate'
            : game.isStalemate() ? 'stalemate'
            : game.isInsufficientMaterial() ? 'insufficient-material'
            : game.isThreefoldRepetition() ? 'threefold-repetition'
            : game.isDraw() ? 'fifty-move-rule'
            : null,
          score: game.isCheckmate() ? (game.turn() === 'w' ? '0-1' : '1-0') : '1/2-1/2'
        };
        onGameEnd?.(result);
      }

      // Persist state if enabled
      if (persistState) {
        localStorage.setItem('chess-game-state', JSON.stringify({
          fen: newPosition,
          history: game.history({ verbose: true }),
          moveNumber: moveNumber
        }));
      }
    } catch (error) {
      console.error('Error updating game state:', error);
    }
  }, [game, onPositionChange, onGameEnd, persistState, moveNumber]);

  const isThreefoldRepetition = useCallback(() => {
    const currentPosition = game.fen().split(' ').slice(0, 4).join(' ');
    return (positionHistory.get(currentPosition) || 0) >= 3;
  }, [game, positionHistory]);

  const isFiftyMoveRule = useCallback(() => {
    const [, , , , halfMoves] = game.fen().split(' ');
    return parseInt(halfMoves) >= 100;
  }, [game]);

  const isInsufficientMaterial = useCallback(() => {
    const pieces = game.board().flat().filter(piece => piece !== null);
    if (pieces.length <= 3) {
      // King vs. King
      if (pieces.length === 2) return true;
      
      // King and Bishop vs. King or King and Knight vs. King
      if (pieces.length === 3) {
        const nonKingPiece = pieces.find(piece => piece?.type !== 'k');
        return nonKingPiece?.type === 'b' || nonKingPiece?.type === 'n';
      }
    }
    return false;
  }, [game]);

  const getPossibleMoves = useCallback((square: string): string[] => {
    try {
      // Get moves in standard format first
      const moves = game.moves({ 
        square: square as Square,
        verbose: false 
      });
      
      // Convert to destination squares
      return moves.map(move => {
        // Handle special notations like "e8=Q" or "O-O"
        if (typeof move === 'string') {
          if (move === 'O-O' || move === 'O-O-O') {
            // Handle castling
            const rank = game.turn() === 'w' ? '1' : '8';
            return move === 'O-O' ? `g${rank}` : `c${rank}`;
          }
          // Extract destination square from algebraic notation
          const match = move.match(/[a-h][1-8]/);
          return match ? match[0] : '';
        }
        return '';
      }).filter(Boolean);
    } catch (e) {
      console.error('Error getting possible moves:', e);
      return [];
    }
  }, [game]);

  const makeMove = useCallback((from: string, to: string, promotion?: PieceSymbol): boolean => {
    try {
      const result = game.move({ from, to, promotion });
      if (result) {
        // Update game state
        updateGameState();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error making move:', error);
      return false;
    }
  }, [game, updateGameState]);

  const undo = useCallback(() => {
    if (!enableUndo) return false;
    
    const undoneMove = game.undo();
    if (undoneMove) {
      redoStack.current.push(undoneMove);
      updateGameState();
      return true;
    }
    return false;
  }, [game, updateGameState, enableUndo]);

  const redo = useCallback(() => {
    if (!enableUndo || redoStack.current.length === 0) return false;
    
    const moveToRedo = redoStack.current.pop();
    if (moveToRedo) {
      const success = game.move(moveToRedo);
      if (success) {
        updateGameState();
        return true;
      }
    }
    return false;
  }, [game, updateGameState, enableUndo]);

  const reset = useCallback(() => {
    game.reset();
    setPositionHistory(new Map());
    updateGameState();
  }, [game, updateGameState]);

  const exportPGN = useCallback(() => {
    return game.pgn();
  }, [game]);

  const validateFen = useCallback((fen: string): boolean => {
    try {
      const chess = new Chess();
      chess.load(fen);
      return true;
    } catch {
      return false;
    }
  }, []);

  const importPGN = useCallback((pgn: string): boolean => {
    try {
      game.loadPgn(pgn);
      updateGameState();
      redoStack.current = [];
      return true;
    } catch (e) {
      console.error('Invalid PGN:', e);
      return false;
    }
  }, [game, updateGameState]);

  // Load persisted state on mount
  useEffect(() => {
    if (persistState) {
      const savedState = localStorage.getItem('chess-game-state');
      if (savedState) {
        try {
          const { fen, history } = JSON.parse(savedState);
          game.load(fen);
          updateGameState();
        } catch (error) {
          console.error('Error loading persisted state:', error);
        }
      }
    }
  }, [persistState, game, updateGameState]);

  // Piece methods
  const validatePieceSymbol = (piece: PieceSymbol): boolean => {
    return Object.keys(PIECE_SVGS).includes(piece);
  };

  const loadTheme = async (name: string) => {
    if (themes[name] || loadingThemes.has(name)) return;

    try {
      setLoadingThemes(prev => new Set([...prev, name]));
      
      let themePieces = createEmptyPieceRecord();
      
      if (name === 'default') {
        themePieces = PIECE_SVGS;
      } else if (name === 'unicode') {
        themePieces = UNICODE_PIECES;
      } else {
        throw new Error(`Theme '${name}' not found`);
      }

      const theme: PieceTheme = {
        name,
        pieces: themePieces
      };

      setThemes(prev => ({
        ...prev,
        [name]: theme
      }));
      setLoadedThemes(prev => new Set([...prev, name]));
      setCurrentTheme(name);
    } catch (error) {
      console.error(`Failed to load theme: ${name}`, error);
      throw error;
    } finally {
      setLoadingThemes(prev => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  const setCustomPiece = useCallback((piece: PieceSymbol, url: string) => {
    if (!validatePieceSymbol(piece)) {
      throw new Error(`Invalid piece symbol: ${piece}`);
    }

    setThemes(prev => ({
      ...prev,
      [currentTheme]: {
        ...prev[currentTheme],
        pieces: {
          ...prev[currentTheme].pieces,
          [piece]: url
        }
      }
    }));
  }, [currentTheme]);

  const resetTheme = useCallback(() => {
    if (currentTheme === 'default') {
      setThemes(prev => ({
        ...prev,
        default: {
          name: 'default',
          pieces: PIECE_SVGS
        }
      }));
    }
  }, [currentTheme]);

  const preloadTheme = async (name: string) => {
    if (loadedThemes.has(name)) return;
    await loadTheme(name);
  };

  const getPieceSvg = useCallback((piece: PieceSymbol): string | null => {
    if (!validatePieceSymbol(piece)) {
      console.error(`Invalid piece symbol: ${piece}`);
      return null;
    }

    const theme = themes[currentTheme];
    if (!theme) {
      console.error(`Theme not loaded: ${currentTheme}`);
      return PIECE_SVGS[piece] || null;
    }

    return theme.pieces[piece] || PIECE_SVGS[piece] || null;
  }, [themes, currentTheme]);

  const getThemeNames = useCallback(() => {
    return Object.keys(themes);
  }, [themes]);

  const isThemeLoaded = useCallback((name: string) => {
    return loadedThemes.has(name);
  }, [loadedThemes]);

  // Initialize themes
  useEffect(() => {
    const init = async () => {
      try {
        await loadTheme('default');
        
        Object.entries(customPieces).forEach(([piece, url]) => {
          setCustomPiece(piece as PieceSymbol, url);
        });

        if (defaultTheme !== 'default') {
          await loadTheme(defaultTheme);
        }

        if (preloadThemes) {
          await Promise.all([
            loadTheme('unicode')
          ]);
        }
      } catch (error) {
        console.error('Error initializing themes:', error);
      }
    };

    init();
  }, [defaultTheme, customPieces, preloadThemes, setCustomPiece]);

  return (
    <ChessContext.Provider value={{
      // Chess context values
      game,
      position,
      isCheck: game.isCheck(),
      isCheckmate: game.isCheckmate(),
      isStalemate: game.isStalemate(),
      isDraw: game.isDraw(),
      isThreefoldRepetition: isThreefoldRepetition(),
      isFiftyMoveRule: isFiftyMoveRule(),
      isInsufficientMaterial: isInsufficientMaterial(),
      turn: game.turn(),
      history,
      moveNumber,
      pgn: game.pgn(),
      makeMove,
      undo,
      redo,
      reset,
      getPossibleMoves,
      exportPGN,
      importPGN,
      getGameState: () => ({
        isCheck: game.isCheck(),
        isCheckmate: game.isCheckmate(),
        isStalemate: game.isStalemate(),
        isDraw: game.isDraw(),
        turn: game.turn() === 'w' ? 'white' : 'black',
        moveNumber,
        halfMoves: game.moveNumber(),
        fen: game.fen(),
        pgn: game.pgn()
      }),
      validateFen,
      // Piece context values
      currentTheme,
      themes,
      loadTheme,
      getPieceSvg,
      setCustomPiece,
      resetTheme,
      preloadTheme,
      getThemeNames,
      isThemeLoaded
    }}>
      {children}
    </ChessContext.Provider>
  );
} 